import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";

import {
  auditLine,
  CHANNEL_LABELS,
  messageTemplateApi,
} from "../../utils/messageTemplateApi";

const useStyles = makeStyles({
  panel: {
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    marginBottom: "16px",
  },
  title: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#101828",
    fontFamily: "monospace",
    wordBreak: "break-word",
  },
  meta: { fontSize: "13px", color: "#475467" },
  row: { display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" },
  grow: { flex: "1 1 320px" },
  touch: { minHeight: "44px" },
  danger: { minHeight: "44px", color: "#b42318", borderColor: "#b42318" },
  requests: { margin: "4px 0 0", paddingLeft: "20px", fontSize: "13px" },
  code: { fontFamily: "monospace" },
});

/**
 * The template as a whole: its description, whether it is sent at all, which qualifying scores
 * use it, and the variables admins requested for it. Changes here add a template-wide history
 * entry and bump only the template's revision, so an open language editor keeps its edits.
 */
const TemplateHeader = ({ detail, definition, onChanged }) => {
  const classes = useStyles();
  const [description, setDescription] = useState(detail.description || "");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    setDescription(detail.description || "");
    setConflict(null);
  }, [detail.id, detail.revision, detail.description]);

  const multiple = Boolean(detail.multipleTemplates);
  const descriptionDirty = (description || "") !== (detail.description || "");

  const fail = (e) => {
    if (e.status === 409) setConflict(e.message);
    else toast.error(e.message);
  };

  const saveDescription = async () => {
    setSaving(true);
    try {
      const saved = await messageTemplateApi.update(detail.id, {
        description,
        revision: detail.revision,
        changeNote: note,
      });
      setNote("");
      toast.success("Description saved.");
      onChanged(saved);
    } catch (e) {
      fail(e);
    } finally {
      setSaving(false);
    }
  };

  const runConfirmed = async () => {
    const { action } = confirm;
    setConfirm(null);
    try {
      const result =
        action === "delete"
          ? await messageTemplateApi.remove(detail.id, detail.revision, note)
          : await messageTemplateApi.reactivate(
              detail.id,
              detail.revision,
              note,
            );
      setNote("");
      toast.success(
        action === "delete" ? "Template deleted." : "Template reactivated.",
      );
      onChanged(result);
    } catch (e) {
      fail(e);
    }
  };

  const pending = detail.pendingVariableRequests || [];

  return (
    <Paper className={classes.panel} component="section" aria-label="Template">
      <Box>
        <Typography component="h1" className={classes.title}>
          {detail.templateName}
          {detail.description ? ` · ${detail.description}` : ""}
        </Typography>
        <Typography className={classes.meta}>
          {CHANNEL_LABELS[detail.channel] || detail.channel} · template #
          {detail.id} · {detail.active ? "active" : "deleted"} · created{" "}
          {auditLine(detail.createdAt, detail.createdByName)} · last change{" "}
          {auditLine(detail.updatedAt, detail.updatedByName)}
          {detail.qualifyingScoreReferences > 0 &&
            ` · used by ${detail.qualifyingScoreReferences} qualifying score(s)`}
        </Typography>
        {definition?.summary && (
          <Typography className={classes.meta}>{definition.summary}</Typography>
        )}
      </Box>

      {!detail.active && (
        <Alert severity="warning">
          This template is deleted: no language of it is sent, and it is hidden
          from pickers. Reactivate it to edit.
        </Alert>
      )}
      {conflict && (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              className={classes.touch}
              onClick={() => {
                setConflict(null);
                onChanged(null);
              }}
            >
              Reload
            </Button>
          }
        >
          {conflict}
        </Alert>
      )}

      <Box className={classes.row}>
        <TextField
          id="mt-template-description"
          label="Description"
          variant="outlined"
          size="small"
          className={classes.grow}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={!detail.active}
          required={multiple}
          helperText={
            multiple
              ? "Required: this is how admins pick the template on a qualifying score."
              : "Optional internal note."
          }
          inputProps={{ maxLength: 255 }}
        />
        <TextField
          id="mt-template-change-note"
          label="Change note (optional)"
          variant="outlined"
          size="small"
          className={classes.grow}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          helperText="Used for the description change, delete or reactivate."
          inputProps={{ maxLength: 500 }}
        />
      </Box>
      <Box className={classes.row}>
        <Button
          variant="contained"
          color="primary"
          className={classes.touch}
          onClick={saveDescription}
          disabled={
            saving ||
            !detail.active ||
            !descriptionDirty ||
            (multiple && !description.trim())
          }
        >
          {saving ? "Saving…" : "Save description"}
        </Button>
        {detail.active ? (
          <Button
            variant="outlined"
            className={classes.danger}
            onClick={() => setConfirm({ action: "delete" })}
          >
            Delete template (all languages)
          </Button>
        ) : (
          <Button
            variant="outlined"
            className={classes.touch}
            onClick={() => setConfirm({ action: "reactivate" })}
          >
            Reactivate template
          </Button>
        )}
      </Box>

      {pending.length > 0 && (
        <Alert severity="info">
          Requested variables waiting for a developer (sent empty until
          connected):
          <ul className={classes.requests}>
            {pending.map((r) => (
              <li key={r.id}>
                <span className={classes.code}>{`{{${r.tag}}}`}</span>
                {r.description ? ` — ${r.description}` : ""}
                {` (${r.localeId || "any language"}, ${auditLine(
                  r.requestedAt,
                  r.requestedByName,
                )})`}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      <Dialog
        open={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        aria-labelledby="mt-template-confirm-title"
      >
        <DialogTitle id="mt-template-confirm-title">
          {confirm?.action === "delete"
            ? "Delete this template in every language?"
            : "Reactivate this template?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm?.action === "delete"
              ? "No language of it will be sent and it disappears from pickers. It stays in history and can be reactivated. To stop only one language, delete that language instead."
              : "Its active languages are sent again and it shows in pickers."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button className={classes.touch} onClick={() => setConfirm(null)}>
            Cancel
          </Button>
          <Button
            className={classes.touch}
            color={confirm?.action === "delete" ? "secondary" : "primary"}
            variant="contained"
            onClick={runConfirmed}
            autoFocus
          >
            {confirm?.action === "delete" ? "Delete" : "Reactivate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

TemplateHeader.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.number,
    templateName: PropTypes.string,
    description: PropTypes.string,
    channel: PropTypes.string,
    active: PropTypes.bool,
    revision: PropTypes.number,
    multipleTemplates: PropTypes.bool,
    qualifyingScoreReferences: PropTypes.number,
    pendingVariableRequests: PropTypes.array,
    createdAt: PropTypes.any,
    createdByName: PropTypes.string,
    updatedAt: PropTypes.any,
    updatedByName: PropTypes.string,
  }).isRequired,
  definition: PropTypes.shape({ summary: PropTypes.string }),
  /** Called with the updated template, or null to ask the parent to reload it. */
  onChanged: PropTypes.func.isRequired,
};

TemplateHeader.defaultProps = {
  definition: null,
};

export default TemplateHeader;
